// SuggestSelectionPopup.js
import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, FormControlLabel, Checkbox,
    createTheme, ThemeProvider
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { groupSizeOptions } from '../data/TravelOptions';
import './SuggestSelectionPopup.css';
import './TravelPreferencesPage.css';
import { Icons } from "../data/Icons";
import DatePickerCalendar from './DatePickerCalendar';

const SuggestSelectionPopup = ({ open, onClose, onSubmit }) => {
    const [SuggestSelectedDate, setSuggestSelectedDate] = useState(null);
    const [SuggestSelectedTime, setSuggestSelectedTime] = useState('');
    const [timeError, setTimeError] = useState(false);
    const [dateError, setDateError] = useState(false);
    const [groupSizeError, setGroupSizeError] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [groupSize, setGroupSize] = useState('');
    const [womenOnly, setWomenOnly] = useState(false);
    const [menOnly, setMenOnly] = useState(false);

    const theme = createTheme({
        direction: 'rtl',
        typography: {
            fontFamily: 'Varela Round, sans-serif',
        },
    });

    // Validate time input (hh:mm, 24-hour, hours 01-23, minutes 00-59)
    const validateTime = (value) => {
        if (!/^\d{2}:\d{2}$/.test(value)) return false;
        const [hh, mm] = value.split(':');
        const hours = Number(hh);
        const minutes = Number(mm);
        return hours >= 1 && hours <= 23 && minutes >= 0 && minutes <= 59;
    };

    // Handle time input change
    const handleTimeChange = (e) => {
        let value = e.target.value.replace(/[^0-9:]/g, '');
        // Prevent more than one colon
        const colonIndex = value.indexOf(':');
        if (colonIndex !== -1) {
            // Remove any additional colons
            value = value.slice(0, colonIndex + 1) + value.slice(colonIndex + 1).replace(/:/g, '');
        }
        // Only auto-insert colon if user is typing forward
        if (
            value.length === 2 &&
            !value.includes(':') &&
            SuggestSelectedTime.length < value.length
        ) {
            value = value + ':';
        }
        setSuggestSelectedTime(value);
        // Don't show error while typing
        if (submitAttempted) {
            setTimeError(value && !validateTime(value));
        } else {
            setTimeError(false);
        }
    };

    // Handle form submission
    const handleSubmit = () => {
        setSubmitAttempted(true);

        const isTimeValid = validateTime(SuggestSelectedTime);
        const isDateValid = SuggestSelectedDate !== null;
        const isGroupValid = groupSize !== null && groupSize !== '';

        setTimeError(!isTimeValid);
        setDateError(!isDateValid);
        setGroupSizeError(!isGroupValid);

        if (!isTimeValid || !isDateValid || !isGroupValid) {
            return;
        }

        // Combine date and time
        const [hours, minutes] = SuggestSelectedTime.split(':');
        const combinedDateTime = new Date(SuggestSelectedDate);
        combinedDateTime.setHours(Number(hours));
        combinedDateTime.setMinutes(Number(minutes));

        onSubmit({
            date: combinedDateTime.toISOString(),
            groupSize,
            womenOnly,
            menOnly
        });

        onClose();
    };

    return (
        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="he">
                <Dialog
                    open={open}
                    onClose={onClose}
                    dir="rtl"
                    className="suggest-dialog"
                    disableScrollLock={true}
                >
                    <div className="suggest-popup-wrapper">
                        <DialogTitle className="suggest-title">
                            הגדרת מאפייני הטיול
                        </DialogTitle>

                        <DialogContent className="suggest-content">
                            {/* Date picker */}
                            <DatePickerCalendar
                                popoverZIndex={5000}
                                value={SuggestSelectedDate}
                                onChange={(date) => setSuggestSelectedDate(date)}
                                label={`${Icons.date} תאריך`}
                                required
                                error={dateError}
                                helperText={dateError ? 'יש לבחור תאריך' : ''}
                                disablePortal
                            />

                            {/* Time input (custom) */}
                            <TextField
                                label={`${Icons.clock} שעה `}
                                value={SuggestSelectedTime}
                                onChange={handleTimeChange}
                                placeholder="hh:mm"
                                fullWidth
                                required
                                inputProps={{
                                    dir: 'rtl',
                                    style: { textAlign: 'right' },
                                    maxLength: 5,
                                    inputMode: 'numeric',
                                    pattern: '[0-9:]*',
                                    onKeyPress: (e) => {
                                        const allowed = /[0-9:]/;
                                        if (!allowed.test(e.key)) {
                                            e.preventDefault();
                                        }
                                        // Prevent entering ':' if already present
                                        if (e.key === ':' && SuggestSelectedTime.includes(':')) {
                                            e.preventDefault();
                                        }
                                    },
                                    onPaste: (e) => {
                                        const paste = (e.clipboardData || window.clipboardData).getData('text');
                                        if (!/^\d{0,2}:?\d{0,2}$/.test(paste)) {
                                            e.preventDefault();
                                        }
                                    }
                                }}
                                error={timeError}
                                helperText={timeError ? 'יש להזין שעה בפורמט hh:mm (שעות 01-23, דקות 00-59)' : ''}
                                margin="normal"
                            />

                            {/* Group size selection */}
                            <TextField className='select-textfield'
                                select
                                fullWidth
                                required
                                error={groupSizeError}
                                helperText={groupSizeError ? 'יש לבחור גודל קבוצה' : ''}
                                label={`${Icons.groupSize} גודל קבוצה `}
                                value={groupSize}
                                onChange={(e) => setGroupSize(e.target.value)}
                                margin="normal"
                                SelectProps={{
                                    native: false,
                                    inputProps: {
                                        dir: 'rtl',
                                        style: { textAlign: 'right' }
                                    }
                                }}
                                InputProps={{
                                    style: { direction: 'rtl', textAlign: 'right' }
                                }}
                            >
                                {groupSizeOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {/* Gender preference checkboxes (in a row) */}
                            {/* <div className="gender-checkboxes">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={womenOnly}
                                            onChange={(e) => {
                                                setWomenOnly(e.target.checked);
                                                if (e.target.checked) setMenOnly(false); // optional: mutually exclusive
                                            }}
                                        />
                                    }
                                    label={` אנשים רק מהמגדר שלי ${Icons.gender}`}
                                />
                            </div> */}
                        </DialogContent>

                        <DialogActions className="suggest-actions">
                            <button
                                className="suggest-btn confirm"
                                onClick={handleSubmit}
                            >
                                אישור
                            </button>

                            <button
                                className="suggest-btn cancel"
                                onClick={onClose}
                            >
                                ביטול
                            </button>
                        </DialogActions>
                    </div>
                </Dialog>
            </LocalizationProvider>
        </ThemeProvider>
    );
};

export default SuggestSelectionPopup;
